// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import translate from "@insite/client-framework/Translate";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { ImgProps } from "@insite/mobius/Img";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { StyledImgWrapper } from "@store/MobiusOverrides/Img/Img";
import { GroupModel, PackageModel } from "@store/Framework/Types/ApiModels";
import PartFinderDrawer from "@store/Library/Components/PartFinder/PartFinderDrawer";
import PartFinderListGroup from "@store/Library/Components/PartFinder/PartFinderListGroup";
import PartFinderModal from "@store/Library/Components/PartFinder/PartFinderModal";
import { ProductBrandStyles } from "@store/Overrides/Components/ProductBrand";
import { productCardProps } from "@store/Themes/common/props/productCard";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import StoreProductContext from "@toolkit/Framework/Components/ProductContext/GetProductContext";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    packageModel?: PackageModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    productToAddToModal: state.store.components.partFinder.productToAddToModal,
    selectedModelPartsData: state.store.data.partFinder.parts,
});

const mapDispatchToProps = {
    loadProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PartFinderListStyles {
    container?: GridContainerProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    partModal?: ModalPresentationProps;
    flexWrapper?: FlexWrapperPresentationProps;
    productQtyFormField?: TextFieldPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    partNumbersStyles?: ProductPartNumbersStyles;
    imgWrapper?: InjectableCss;
    partNumber?: TypographyProps;
    note?: TypographyProps;
    img?: ImgProps;
    spinnerGridItem?: GridItemProps;
    spinner?: LoadingSpinnerProps;
}

export const partFinderListStyles: PartFinderListStyles = {
    container: {
        css: css`
            width: 100%;
            padding: ${pxToRem(15)};
            background-color: ${getColor("common.accent")};
        `,
    },
    accordion: {
        css: css`
            width: 100%;
        `,
    },
    accordionSection: {
        panelProps: {
            css: css`
                ${x => x.theme.accordion.sectionDefaultProps?.panelProps?.css}
                background: ${getColor("header.main")};
                overflow-x: auto;
                padding-left: 1rem !important;
                padding-right: 1rem !important;
            `,
        },
        headerProps: {
            css: css`
                ${x => x.theme.accordion.sectionDefaultProps?.headerProps?.css}
                button {
                    background: ${getColor("common.borderLight")} !important;
                }
            `,
        },
    },
    partModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalContainer: css`
                text-align: center;
            `,
        },
    },
    flexWrapper: {
        gap: pxToRem(10),
        extendedStyles: {
            wrapper: {
                css: css`
                    margin-top: ${pxToRem(10)};
                `,
            },
            content: {
                css: css`
                    justify-content: center;
                `,
            },
        },
    },
    productQtyFormField: {
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                flex-direction:row-reverse;

                > label {
                    align-self: center;
                    padding: ${pxToRem(7, 20, 0, 10)};
                }
            `,
        },
    },
    addToCartButton: {
        variant: "secondary",
        css: css`
            align-self: flex-end;
            white-space: nowrap;
            text-transform: uppercase;
        `,
    },
    brandStyles: productCardProps.brand,
    descriptionStyles: productCardProps.description,
    partNumbersStyles: productCardProps.partNumbers,
    imgWrapper: {
        css: css`
            ${StyledImgWrapper} {
                justify-content: center;
            }
        `,
    },
    note: {
        italic: true,
        size: 12,
    },
    partNumber: {
        css: css`
            display: block;
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    spinnerGridItem: { width: 12 },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = partFinderListStyles;

const PartFinderList = ({ packageModel, productToAddToModal, selectedModelPartsData }: Props) => {
    const [openedGroups, setOpenedGroups] = useState<string[]>([]);

    useEffect(() => {
        setOpenedGroups([]);
        if (packageModel?.groups?.[0]?.name) {
            setOpenedGroups([packageModel.groups[0].name]);
        }
        return () => setOpenedGroups([]);
    }, [packageModel]);

    const onAccordionToggle = (groupKey: string, expanded: boolean) => {
        if (!groupKey) {
            return;
        }

        setOpenedGroups(prevGroups => {
            if (expanded) {
                return prevGroups.includes(groupKey) ? prevGroups : [...prevGroups, groupKey];
            }
            return prevGroups.filter(group => group !== groupKey);
        });
    };

    const showAccordion = (group: GroupModel, index: number) => {
        if (openedGroups.length > 0) {
            return openedGroups.indexOf(group.name) > -1;
        }
        return index === 0; // fallback for initialExpanded on first paint
    };

    if (selectedModelPartsData?.packages && selectedModelPartsData?.packages[0].number !== packageModel?.number) {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.spinnerGridItem}>
                    <LoadingSpinner {...styles.spinner} />
                </GridItem>
            </GridContainer>
        );
    }

    if (!packageModel) {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.spinnerGridItem}>
                    <LoadingSpinner {...styles.spinner} />
                </GridItem>
            </GridContainer>
        );
    }
    return (
        <>
            <PartFinderDrawer />
            {productToAddToModal && (
                <StoreProductContext product={productToAddToModal.product} loadPrice={true} loadInventory={true}>
                    <PartFinderModal />
                </StoreProductContext>
            )}
            <GridContainer {...styles.container}>
                <GridItem width={12}>
                    <Accordion headingLevel={2} {...styles.accordion}>
                        {packageModel?.groups.length === 0 && (
                            <Typography> {translate("Store_PartList_Nothing_Found")}</Typography>
                        )}
                        {packageModel?.groups?.map((group, index) =>
                            group.name ? (
                                <ManagedAccordionSection
                                    {...styles.accordionSection}
                                    title={group.name}
                                    key={`accordion-${packageModel?.number}-${group.name}`}
                                    initialExpanded={showAccordion(group, index)}
                                    expanded={showAccordion(group, index)}
                                    onTogglePanel={expanded => onAccordionToggle(group.name, expanded)}
                                >
                                    {showAccordion(group, index) && (
                                        <PartFinderListGroup
                                            key={`group-${packageModel?.number}-${group.name}`}
                                            group={group}
                                            packageModel={packageModel}
                                        />
                                    )}
                                </ManagedAccordionSection>
                            ) : null,
                        )}
                    </Accordion>
                </GridItem>
                <GridItem width={12}>
                    <Typography {...styles.note}>{translate("Store_ProductDetail_PartsList_Note")}</Typography>
                </GridItem>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PartFinderList);
